// Equation Box: a jar of number and sign gems. Build any equation that hits the target.
// From the sketch in the notes. Any valid equation counts; longer ones pay more.

import { h, clear } from '@kit/dom';
import type { GameModule, GameContext } from '@shell/types';
import { makeJar, type Jar } from '@problems/equation-box';
import { evaluate, tokensToString, type Tok } from '@problems/expr';

const TARGETS_PER_ROUND = 5;
const GEM_COLORS = ['#e2574c', '#f08a3c', '#f5c542', '#5fbf6a', '#3bb3a9', '#4f8fe6', '#9b6be6', '#ee86b7'];

function mount(host: HTMLElement, ctx: GameContext) {
  let n = 0;
  let jar!: Jar;
  let box: { tok: Tok; jarIndex?: number }[] = [];
  let usedJar = new Set<number>();
  let wrongs = 0;
  let hinted = false;
  let attempts = 0, correct = 0, bestGems = 0;
  const t0 = performance.now();
  let tProblem = performance.now();
  let level = ctx.currentLevel();

  const head = h('div.row', { style: { justifyContent: 'space-between' } }, h('span.muted', null, 'Target ', h('strong', null, '1'), ` of ${TARGETS_PER_ROUND}`), h('span.muted.small', null, ''));
  const target = h('div.center', null, h('div.muted.small', null, 'Make'), h('div.big', { style: { fontSize: '44px', color: 'var(--math)' } }, '0'));
  const xClue = h('div.center.muted', { style: { fontWeight: '800' } });
  const boxEl = h('div.card', { style: { minHeight: '64px', display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center', justifyContent: 'center', borderStyle: 'dashed' } });
  const live = h('div.center.muted', { style: { fontWeight: '800', minHeight: '24px' } });
  const numbers = h('div.row.wrap', { style: { justifyContent: 'center', gap: '8px' } });
  const ops = h('div.row.wrap', { style: { justifyContent: 'center', gap: '8px' } });
  const actions = h('div.grid2', null,
    h('button.btn.ghost', { onclick: clearBox }, 'Clear'),
    h('button.btn.primary', { onclick: check }, 'Check'),
  );
  const showBtn = h('button.btn.small.ghost.block', { onclick: showSolution, style: { display: 'none' } }, 'Show me one way');
  host.append(head, target, xClue, boxEl, live, numbers, ops, actions, showBtn);

  function newTarget() {
    level = ctx.currentLevel();
    jar = makeJar(level, ctx.rng);
    box = []; usedJar = new Set(); wrongs = 0; hinted = false;
    tProblem = performance.now();
    showBtn.style.display = 'none';
    (head.querySelector('strong') as HTMLElement).textContent = String(n + 1);
    (target.lastElementChild as HTMLElement).textContent = String(jar.target);
    xClue.textContent = jar.x ? `The x gem is a mystery number: ${jar.x.clue}` : '';
    render();
  }

  function gemBtn(label: string, color: string, onclick: () => void, disabled = false): HTMLElement {
    return h('button', {
      disabled,
      onclick,
      style: { width: '52px', height: '52px', borderRadius: '50%', border: 'none', background: color, color: '#fff', fontWeight: '900', fontSize: label.length > 2 ? '16px' : '22px', opacity: disabled ? '.25' : '1', boxShadow: 'inset 0 -4px 0 rgba(0,0,0,.18)' },
    }, label);
  }

  function render() {
    clear(numbers); clear(ops); clear(boxEl);
    jar.numbers.forEach((v, i) => numbers.append(gemBtn(String(v), GEM_COLORS[v % GEM_COLORS.length], () => add({ t: 'n', v }, i), usedJar.has(i))));
    if (jar.x) numbers.append(gemBtn('x', '#2a2a2e', () => add({ t: 'x' }, -1), box.some(b => b.tok.t === 'x')));
    const opDefs: [Tok, string][] = [[{ t: 'op', v: '+' }, '+'], [{ t: 'op', v: '-' }, '−']];
    if (jar.ops.includes('*')) opDefs.push([{ t: 'op', v: '*' }, '×']);
    if (jar.ops.includes('/')) opDefs.push([{ t: 'op', v: '/' }, '÷']);
    if (jar.parens) { opDefs.push([{ t: '(' }, '(']); opDefs.push([{ t: ')' }, ')']); }
    for (const [tok, label] of opDefs) ops.append(gemBtn(label, '#9a9aa3', () => add(tok)));
    if (!box.length) boxEl.append(h('span.muted', null, 'Tap gems to drop them in the box'));
    box.forEach((b, i) => {
      const label = b.tok.t === 'n' ? String(b.tok.v) : b.tok.t === 'x' ? 'x' : b.tok.t === 'op' ? ({ '+': '+', '-': '−', '*': '×', '/': '÷' })[b.tok.v] : b.tok.t;
      const color = b.tok.t === 'n' ? GEM_COLORS[b.tok.v % GEM_COLORS.length] : b.tok.t === 'x' ? '#2a2a2e' : '#9a9aa3';
      boxEl.append(h('button', {
        onclick: () => { box.splice(i, 1); if (b.jarIndex !== undefined) usedJar.delete(b.jarIndex); render(); },
        style: { width: '44px', height: '44px', borderRadius: '50%', border: 'none', background: color, color: '#fff', fontWeight: '900', fontSize: '20px' },
      }, label));
    });
    const r = evaluate(box.map(b => b.tok), jar.x?.value);
    live.textContent = box.length ? (r.ok ? `= ${r.value}` : '…') : '';
    live.style.color = r.ok && r.value === jar.target ? 'var(--good)' : '';
  }

  function add(tok: Tok, jarIndex?: number) {
    if (tok.t === 'n' && jarIndex !== undefined) usedJar.add(jarIndex);
    box.push({ tok, jarIndex: tok.t === 'n' ? jarIndex : undefined });
    ctx.sound('pop');
    render();
  }

  function clearBox() { box = []; usedJar = new Set(); render(); }

  function check() {
    if (!box.length) { ctx.say('The box is empty. Drop some gems in.'); return; }
    const toks = box.map(b => b.tok);
    const r = evaluate(toks, jar.x?.value);
    const ms = performance.now() - tProblem;
    if (!r.ok) { ctx.say(`Hmm, ${r.reason}.`); ctx.sound('buzz'); return; }
    const numGems = toks.filter(t => t.t === 'n' || t.t === 'x').length;
    if (r.value === jar.target) {
      attempts++; correct++;
      bestGems = Math.max(bestGems, numGems);
      ctx.report({ skill: numGems >= 3 && jar.ops.includes('*') ? 'mult_div' : jar.x ? 'algebra' : 'add_sub', level, correct: true, hintUsed: hinted, ms, problemId: `eq:${jar.target}` });
      ctx.sound(numGems >= 4 ? 'chime' : 'ding');
      ctx.say(numGems >= 4 ? `${tokensToString(toks)} = ${jar.target}. ${numGems} gems! Fancy.` : `${tokensToString(toks)} = ${jar.target}. Yes!`);
      if (numGems >= 5) ctx.charm('charm.eq5');
      if (toks.some(t => t.t === 'x')) ctx.charm('charm.eqx');
      n++;
      if (n >= TARGETS_PER_ROUND) { setTimeout(() => ctx.end({ attempts, correct, perfect: attempts === correct, durationMs: performance.now() - t0, notes: [`Most gems in one equation: ${bestGems}`] }), 900); return; }
      setTimeout(newTarget, 1000);
    } else {
      attempts++; wrongs++;
      ctx.report({ skill: 'add_sub', level, correct: false, hintUsed: hinted, ms, problemId: `eq:${jar.target}` });
      ctx.sound('buzz');
      const diff = jar.target - r.value;
      ctx.say(`That makes ${r.value}. You need ${jar.target}, which is ${Math.abs(diff)} ${diff > 0 ? 'more' : 'less'}.`);
      if (wrongs >= 2) showBtn.style.display = '';
    }
  }

  function showSolution() {
    hinted = true;
    ctx.say(`One way: ${tokensToString(jar.solution)} = ${jar.target}. Now build it, or find your own.`);
    showBtn.style.display = 'none';
  }

  newTarget();
  return { destroy() { clear(host); } };
}

const mod: GameModule = {
  id: 'math.equation-box',
  title: 'Equation Box',
  blurb: 'Drop number and sign gems into the box to hit the target. Any true equation counts.',
  category: 'math',
  skills: ['add_sub', 'mult_div', 'algebra'],
  levelRange: [1, 10],
  unlock: 'always',
  mount,
};
export default mod;
